
import { getCachedItem, setCachedItem } from '@/utils/clientCacheUtils';
import { CLIENT_CACHE_TTL } from './cache-constants';
import { CACHE_RELATIONSHIPS_STORE } from './cache-constants';

// Track content relationships for sophisticated cache invalidation
export const trackContentRelationship = async (
  sourceKey: string,
  sourceType: string,
  relatedKeys: string[],
  relatedType: string
): Promise<boolean> => {
  try {
    console.log(`[CACHE] Tracking relationship: ${sourceKey} (${sourceType}) → ${relatedKeys.length} items`);
    await setCachedItem(
      `${sourceKey}_${sourceType}_relationships`,
      { relatedKeys, relatedType, lastUpdated: new Date() },
      CACHE_RELATIONSHIPS_STORE,
      { maxAge: 30 * 24 * 60 * 60 * 1000 } // 30 days
    );
    return true;
  } catch (error) {
    console.error('[CACHE ERROR] Error tracking content relationship:', error);
    return false;
  }
};

// Get related content keys
export const getRelatedContentKeys = async (
  sourceKey: string,
  sourceType: string
): Promise<{relatedKeys: string[], relatedType: string} | null> => {
  try {
    console.log(`[CACHE] Getting related content for: ${sourceKey} (${sourceType})`);
    return await getCachedItem(
      `${sourceKey}_${sourceType}_relationships`, 
      CACHE_RELATIONSHIPS_STORE
    );
  } catch (error) {
    console.error('[CACHE ERROR] Error getting related content keys:', error);
    return null;
  }
};

// Update item popularity (for adaptive TTL)
export const updateItemPopularity = async (
  key: string,
  storeName: string
): Promise<void> => {
  try {
    const item = await getCachedItem<{popularity?: number}>(key, storeName);
    if (item && typeof item === 'object' && item !== null) {
      // Safely access/modify the popularity property
      const popularity = (item.popularity || 0) + 1;
      item.popularity = popularity;
      
      console.log(`[CACHE] Updating popularity for ${key} in ${storeName}: ${popularity}`);
      
      // Calculate adaptive TTL based on popularity
      let adaptiveTtl = CLIENT_CACHE_TTL.RESPONSE;
      
      if (popularity >= 5) {
        // Frequently used items get longer TTL
        adaptiveTtl = CLIENT_CACHE_TTL.FREQUENTLY_USED;
        console.log(`[CACHE] Frequently used item detected (${popularity}), extending TTL to ${adaptiveTtl/1000/60/60}h`);
      } else if (popularity <= 1) {
        // Rarely used items get shorter TTL
        adaptiveTtl = CLIENT_CACHE_TTL.RARELY_USED;
        console.log(`[CACHE] Rarely used item (${popularity}), shortening TTL to ${adaptiveTtl/1000/60/60}h`);
      }
      
      // Update the item with new popularity and TTL
      await setCachedItem(key, item, storeName, { maxAge: adaptiveTtl });
    }
  } catch (error) {
    console.error('[CACHE ERROR] Error updating item popularity:', error);
  }
};

// Create a debug function to check cache status
export const debugCacheStatus = async (storeName: string): Promise<void> => {
  try {
    const keys = await getCachedKeys(storeName);
    console.log(`[CACHE DEBUG] ${storeName} contains ${keys.length} items:`, keys);
    
    // Sample a few items
    if (keys.length > 0) {
      const sampleSize = Math.min(keys.length, 3);
      for (let i = 0; i < sampleSize; i++) {
        const key = keys[i];
        const item = await getCachedItem(key, storeName);
        console.log(`[CACHE DEBUG] Sample item ${i+1} from ${storeName}:`, { key, item });
      }
    }
  } catch (error) {
    console.error(`[CACHE ERROR] Error debugging cache status for ${storeName}:`, error);
  }
};

// Helper function to get all keys in a store
async function getCachedKeys(storeName: string): Promise<string[]> {
  try {
    if ('caches' in window) {
      const cache = await caches.open(storeName);
      const keys = await cache.keys();
      return keys.map(request => {
        // Extract the key from request URL
        const url = new URL(request.url);
        return url.pathname.substring(1); // Remove the leading '/'
      });
    }
    return [];
  } catch (error) {
    console.error(`[CACHE ERROR] Error getting keys for ${storeName}:`, error);
    return [];
  }
}
